import { useEffect, useRef, useState } from "react";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Calendar as CalendarIcon, ChevronDown } from "lucide-react";
import { localIsoDay } from "@/lib/dates";

/**
 * Cross-platform date picker. Replaces native <input type="date"> which has
 * known iOS Safari quirks inside Radix dialogs (tap doesn't reliably open
 * the native picker). Renders a button showing the date in NZ format; tap
 * to reveal an inline Calendar below it. Behaves identically on
 * iOS/Android/Desktop, and avoids the nested-modal issues you get when
 * stacking a Radix Popover inside a Radix Dialog.
 *
 * Value & onChange use ISO YYYY-MM-DD strings so it's a drop-in replacement
 * for <Input type="date" value=... onChange=.../> patterns.
 */
const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function prettyLabel(iso) {
  if (!iso) return "Pick a date";
  try {
    const d = new Date(iso + "T00:00:00");
    return `${DAY_NAMES[d.getDay()]} ${d.getDate()} ${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`;
  } catch {
    return iso;
  }
}

export function DateField({ value, onChange, className = "", testId = "date-field", min, max, disabled }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const selected = value ? new Date(value + "T00:00:00") : undefined;
  // Close on outside click. Doesn't fight with a Radix dialog because
  // we're using a plain mousedown listener, not another Radix overlay.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [open]);
  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <Button
        type="button"
        variant="outline"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full justify-between rounded-sm border-zinc-400 font-mono text-sm h-10 px-3"
        data-testid={testId}
      >
        <span className="inline-flex items-center gap-2">
          <CalendarIcon className="w-4 h-4 text-zinc-500" />
          <span className={value ? "" : "text-zinc-400"}>{prettyLabel(value)}</span>
        </span>
        <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </Button>
      {open && (
        <div
          className="absolute z-50 left-0 right-0 mt-1 border-2 border-zinc-900 dark:border-zinc-700 bg-white dark:bg-[#151C27] shadow-[3px_3px_0_0_#FF5F15]"
          data-testid={`${testId}-calendar`}
        >
          <Calendar
            mode="single"
            selected={selected}
            onSelect={(d) => {
              if (!d) return;
              onChange?.(localIsoDay(d));
              setOpen(false);
            }}
            fromDate={min ? new Date(min + "T00:00:00") : undefined}
            toDate={max ? new Date(max + "T00:00:00") : undefined}
            initialFocus
          />
        </div>
      )}
    </div>
  );
}

export default DateField;
